import dayjs from 'dayjs';
import { Avatar, Dropdown } from 'flowbite-react';
import React from 'react';
import { HiOutlineArrowLeft, HiOutlineBriefcase, HiOutlineLogout, HiOutlineCurrencyRupee, HiOutlineLocationMarker, HiOutlineUser } from 'react-icons/hi';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { signout } from 'src/apis/auth';
import { STUDENT_APP_ENDPOINT } from 'src/config/env.config';
import { useUserContext } from 'src/context/UserProvider';
import { stringAvatar } from 'src/utils';
import {
	PROFILE_MENU_CLASS,
	PROFILE_MENU_DANGER_ITEM_CLASS,
	PROFILE_MENU_HEADER_CLASS,
	PROFILE_MENU_ITEM_CLASS,
} from 'src/components/shared/profileMenuStyles';
import { JobseekerDrivePosition } from 'types';

const FONT_LINK_ID = 'plus-jakarta-sans-font';
const ANIMATION_STYLE_ID = 'placement-drive-animations';

// Subtle motion shared by the drive screens (same curves as the design prototype).
const ANIMATION_CSS = `
/* Opacity only: a transform on the page container would make position:fixed children (action bars, modals) position relative to it while it animates. */
@keyframes dp-fade { 0% { opacity: 0; } 100% { opacity: 1; } }
@keyframes dp-pop { 0% { opacity: 0; transform: scale(.7); } 100% { opacity: 1; transform: scale(1); } }
@keyframes dp-rise { 0% { opacity: 0; transform: translateY(12px); } 100% { opacity: 1; transform: none; } }
@keyframes dp-ring { 100% { opacity: 1; transform: scale(1); } }
@keyframes dp-pulse { 0% { opacity: .7; transform: scale(1); } 100% { opacity: 0; transform: scale(1.6); } }
@keyframes dp-draw { 100% { stroke-dashoffset: 0; } }
@keyframes dp-spin { 100% { transform: rotate(360deg); } }
@keyframes dp-sheet { 0% { opacity: 0; transform: translateY(28px) scale(.98); } 100% { opacity: 1; transform: none; } }
@keyframes dp-backdrop { 0% { opacity: 0; } 100% { opacity: 1; } }
.dp-fade { animation: dp-fade .28s cubic-bezier(.32,.72,0,1); }
.dp-pop { animation: dp-pop .42s cubic-bezier(.32,.72,0,1); }
.dp-rise { opacity: 0; animation: dp-rise .4s cubic-bezier(.32,.72,0,1) forwards; }
.dp-ring { opacity: 0; transform: scale(.4); animation: dp-ring .45s cubic-bezier(.32,.72,0,1) forwards; }
.dp-pulse { animation: dp-pulse 1.1s cubic-bezier(.32,.72,0,1) .3s forwards; }
.dp-spin { animation: dp-spin 1s linear infinite; }
.dp-sheet { animation: dp-sheet .34s cubic-bezier(.32,.72,0,1); }
.dp-backdrop { animation: dp-backdrop .25s ease-out; }
.dp-lift { transition: transform .2s cubic-bezier(.32,.72,0,1), box-shadow .2s, border-color .2s, background-color .2s; }
.dp-lift:hover { transform: translateY(-2px); }
.dp-form label { font-size: 13px; font-weight: 700; color: #111E2C; }
.dp-form input:not([type='checkbox']):not([type='radio']):not([type='file']), .dp-form textarea {
	font-size: 15px; border-radius: 12px; border: 1.5px solid #E3E3EC; background: #fff; padding: 12px 14px; color: #111E2C; box-shadow: none; transition: border-color .15s, box-shadow .15s;
}
.dp-form input:not([type='checkbox']):not([type='radio']):not([type='file']):focus, .dp-form textarea:focus { outline: none; border-color: #4F46E5; box-shadow: 0 0 0 4px #EEEDFD; }
.dp-form [class*='-control'] { min-height: 48px; border-radius: 12px !important; border: 1.5px solid #E3E3EC !important; box-shadow: none !important; }
.dp-form [class*='-control'][class*='--is-focused'] { border-color: #4F46E5 !important; box-shadow: 0 0 0 4px #EEEDFD !important; }
.dp-form .quill { border: 1.5px solid #E3E3EC !important; border-radius: 14px !important; overflow: hidden; background: #fff; transition: border-color .15s, box-shadow .15s; }
.dp-form .quill:focus-within { border-color: #4F46E5; box-shadow: 0 0 0 4px #EEEDFD; }
.dp-form .ql-toolbar.ql-snow { display: flex; flex-wrap: wrap; align-items: center; gap: 2px; border: 0; border-bottom: 1px solid #EEEEF5; background: #FAFAFD; padding: 8px 10px; font-family: inherit; }
.dp-form .ql-toolbar.ql-snow .ql-formats { display: inline-flex; align-items: center; margin-right: 8px; }
.dp-form .ql-toolbar.ql-snow button { width: 30px; height: 30px; border-radius: 8px; padding: 5px; transition: background-color .15s; }
.dp-form .ql-toolbar.ql-snow button:hover, .dp-form .ql-toolbar.ql-snow button.ql-active, .dp-form .ql-toolbar.ql-snow .ql-picker-label:hover { background: #EEEDFD; }
.dp-form .ql-toolbar.ql-snow button:hover .ql-stroke, .dp-form .ql-toolbar.ql-snow button.ql-active .ql-stroke { stroke: #4F46E5; }
.dp-form .ql-toolbar.ql-snow button:hover .ql-fill, .dp-form .ql-toolbar.ql-snow button.ql-active .ql-fill { fill: #4F46E5; }
.dp-form .ql-toolbar.ql-snow .ql-picker { font-size: 13px; font-weight: 600; color: #3C3E55; }
.dp-form .ql-toolbar.ql-snow .ql-picker-label { border: 0; border-radius: 8px; padding: 4px 8px; }
.dp-form .ql-container.ql-snow { border: 0; font-family: inherit; font-size: 15px; }
.dp-form .ql-editor { padding: 14px 16px; color: #111E2C; line-height: 1.55; }
.dp-form .ql-editor.ql-blank::before { left: 16px; right: 16px; font-style: normal; color: #9A9BB0; }
.dark .dp-form .quill { background: #374151; border-color: #4B5563; }
.dark .dp-form .ql-toolbar.ql-snow { background: #1F2A37; border-color: #4B5563; }
.dark .dp-form .ql-editor { color: #fff; }
/* Vaul adds an ::after block under bottom drawers (to hide overscroll); it shows as a white slab under the floating desktop panel. */
@media (min-width: 961px) { .dp-drawer-float::after { display: none !important; } }
/* Application detail pages (assignment, online test, offer, updated terms): restyle the legacy markup without rewriting it. */
.dp-detail { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; }
.dp-detail h1, .dp-detail h2 { font-family: inherit; font-weight: 800; letter-spacing: -0.02em; color: #111E2C; }
.dp-detail h3 { font-family: inherit; font-weight: 700; color: #111E2C; }
.dp-detail button[class*='bg-[#7777E7]'] { border: 0 !important; border-radius: 12px !important; background: linear-gradient(180deg,#5B4FF0,#3027D8) !important; box-shadow: 0 4px 12px rgba(79,70,229,.25); font-weight: 700; }
.dp-detail button[class*='bg-[#7777E7]']:disabled { opacity: .5; box-shadow: none; }
.dp-detail a[class*='bg-[gray]'], .dp-detail button[class*='bg-[gray]'], .dp-detail [class*='bg-[#18222D]'][class*='rounded-[24px]'] { background: #EEEDFD !important; color: #3730A3 !important; font-weight: 600; border-radius: 999px !important; }
.dp-detail [class*='border-dashed'] { border-color: #C9C5FA !important; background: #FAFAFF; border-radius: 16px !important; }
.dp-detail [class*='bg-[#E5F0FF]'], .dp-detail [class*='bg-blue-50'] { background: #F6F5FF !important; border: 1px solid #E8E6FD !important; border-radius: 16px !important; }
.dp-detail button[class*='bg-white'][class*='border'] { border-radius: 12px; }
@media (prefers-reduced-motion: reduce) {
	.dp-fade, .dp-pop, .dp-rise, .dp-ring, .dp-pulse, .dp-sheet, .dp-backdrop { animation: none; opacity: 1; transform: none; }
	.dp-lift:hover { transform: none; }
}
`;

// The placement drive screens are designed in Plus Jakarta Sans.
export const DRIVE_FONT_STYLE: React.CSSProperties = { fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" };

export const useDriveFont = () => {
	React.useEffect(() => {
		// Always (re)write the rules so an already-injected tag from an earlier load/HMR can't keep serving stale CSS.
		let style = document.getElementById(ANIMATION_STYLE_ID) as HTMLStyleElement | null;
		if (!style) {
			style = document.createElement('style');
			style.id = ANIMATION_STYLE_ID;
			document.head.appendChild(style);
		}
		if (style.textContent !== ANIMATION_CSS) style.textContent = ANIMATION_CSS;
		if (document.getElementById(FONT_LINK_ID)) return;
		const link = document.createElement('link');
		link.id = FONT_LINK_ID;
		link.rel = 'stylesheet';
		link.href = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap';
		document.head.appendChild(link);
	}, []);
};

const LOGO_COLORS = ['#7C3AED', '#2563EB', '#16A34A', '#DC2626', '#EA580C', '#0891B2', '#DB2777', '#4F46E5'];

export const CompanyLogo = ({ name, logo, url, size = 44 }: { name?: string; logo?: string; url?: string; size?: number }) => {
	const [hasError, setHasError] = React.useState(false);
	// Same fallback as the rest of the app: stored logo first, else the logo service keyed on the company website.
	const logoSrc = logo || (url ? `https://logos.hunter.io/${url}` : '');
	const initial = (name || '?').trim().charAt(0).toUpperCase();
	const color = LOGO_COLORS[(name || '').length % LOGO_COLORS.length];
	if (logoSrc && !hasError) {
		return (
			<img
				src={logoSrc}
				alt={name}
				onError={() => setHasError(true)}
				style={{ width: size, height: size }}
				className="rounded-[10px] object-contain bg-white border border-[#EEEEF5] shrink-0"
			/>
		);
	}
	return (
		<div
			style={{ width: size, height: size, background: color, fontSize: size * 0.42 }}
			className="rounded-[10px] text-white font-[600] flex items-center justify-center shrink-0"
		>
			{initial}
		</div>
	);
};

// Shows the start date; a multi-day drive (end date on a different day) shows start and end stacked, e.g. "18 OCT" over "03 NOV".
export const DateBadge = ({ date, endDate }: { date: Date | string; endDate?: Date | string | null }) => {
	const isRange = !!endDate && !dayjs(endDate).isSame(date, 'day');
	const box = 'w-[64px] h-[64px] rounded-[12px] bg-[#EEEDFD] border border-[#D9D8F7] flex flex-col items-center justify-center shrink-0';
	if (!isRange) {
		return (
			<div className={box}>
				<span className="text-[22px] font-[700] leading-[24px] text-[#3730A3]">{dayjs(date).format('DD')}</span>
				<span className="text-[12px] font-[700] tracking-wide text-[#3730A3]">{dayjs(date).format('MMM').toUpperCase()}</span>
			</div>
		);
	}
	const Day = ({ value }: { value: Date | string }) => (
		<span className="flex items-baseline gap-1 leading-[17px] text-[#3730A3]">
			<span className="text-[16px] font-[700]">{dayjs(value).format('DD')}</span>
			<span className="text-[10px] font-[700] tracking-wide">{dayjs(value).format('MMM').toUpperCase()}</span>
		</span>
	);
	return (
		<div className={`${box} gap-[3px]`}>
			<Day value={date} />
			<span className="h-px w-5 bg-[#C4C2F0]" />
			<Day value={endDate as Date | string} />
		</div>
	);
};

export const formatSalary = (salary?: { min: number; max: number }) => {
	if (!salary || (!salary.min && !salary.max)) return 'Not disclosed';
	return salary.min === salary.max ? `₹${salary.max} LPA` : `₹${salary.min}–${salary.max} LPA`;
};

export const formatPositionTitle = (title?: string) =>
	(title || '')
		.replace(/_/g, ' ')
		.toLowerCase()
		.replace(/^\w/, c => c.toUpperCase());

const WORKING_MODE_LABELS: Record<string, string> = {
	fulltime: 'Full-time',
	parttime: 'Part-time',
	internship: 'Internship',
	internshiptofulltime: 'Internship to full-time',
	contract: 'Contract',
};

export const formatWorkingMode = (mode?: string) => {
	const key = (mode || '').toLowerCase().replace(/[^a-z]/g, '');
	return WORKING_MODE_LABELS[key] || formatPositionTitle(mode);
};

export const PositionSubtitle = ({ position }: { position: JobseekerDrivePosition }) => (
	<>
		{formatPositionTitle(position.title)} · {formatSalary(position.salary)}
		{position.vacancy ? ` · ${position.vacancy} opening${position.vacancy > 1 ? 's' : ''}` : ''}
	</>
);

export const StatusPill = ({ status }: { status?: JobseekerDrivePosition['applicationStatus'] }) => {
	const config = {
		applied: { text: 'Under review', className: 'bg-[#EEEDFD] text-[#3730A3]' },
		shortlisted: { text: 'Shortlisted', className: 'bg-[#E6F6EC] text-[#1E7A44]' },
		rejected: { text: 'Not selected', className: 'bg-[#FDECEC] text-[#B42318]' },
	}[status || 'applied'];
	return <span className={`px-3 py-1 rounded-full text-[13px] font-[600] whitespace-nowrap ${config.className}`}>{config.text}</span>;
};

export const PrimaryButton = ({
	children,
	className = '',
	...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
	<button
		{...props}
		className={`rounded-[12px] bg-gradient-to-b from-[#5B4FF0] to-[#3027D8] text-white font-[600] py-3 px-6 shadow-[0_6px_16px_rgba(79,70,229,0.3)] disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
	>
		{children}
	</button>
);

export const useIsMobile = () => {
	const query = '(max-width: 1023px)';
	const [isMobile, setIsMobile] = React.useState(() => (typeof window !== 'undefined' ? window.matchMedia(query).matches : false));
	React.useEffect(() => {
		const media = window.matchMedia(query);
		const onChange = () => setIsMobile(media.matches);
		onChange();
		media.addEventListener('change', onChange);
		return () => media.removeEventListener('change', onChange);
	}, []);
	return isMobile;
};

export const PositionChip = ({ icon: Icon, text }: { icon: React.ElementType; text: string }) => (
	<span className="inline-flex items-center gap-1.5 rounded-full bg-[#F1F1F5] dark:bg-[#111827] px-2.5 py-1 text-[12.5px] leading-[18px] font-[500] text-[#3C3E55] dark:text-[#B0B8C4]">
		<Icon className="h-3.5 w-3.5 shrink-0 text-[#8A8BA3]" />
		{text}
	</span>
);

export const PositionChips = ({ position, children }: { position: JobseekerDrivePosition; children?: React.ReactNode }) => (
	<div className="mt-2.5 flex flex-wrap gap-1.5">
		<PositionChip icon={HiOutlineCurrencyRupee} text={formatSalary(position.salary)} />
		{position.workLocations?.[0] ? <PositionChip icon={HiOutlineLocationMarker} text={position.workLocations[0].split(',')[0].trim()} /> : null}
		{position.vacancy ? <PositionChip icon={HiOutlineUser} text={`${position.vacancy} opening${position.vacancy > 1 ? 's' : ''}`} /> : null}
		{position.workingMode ? <PositionChip icon={HiOutlineBriefcase} text={formatWorkingMode(position.workingMode)} /> : null}
		{children}
	</div>
);

// Fixed action bar above the bottom navigation, mobile only.
export const MobileActionBar = ({ children }: { children: React.ReactNode }) => (
	<div className="lg:hidden fixed inset-x-0 bottom-0 z-20 px-[4%] pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 bg-white dark:bg-[#1F2937] border-t border-[#EEEEF5] dark:border-[#374151]">
		{children}
	</div>
);

// Initials avatar with the profile menu (My Profile, Logout).
export const ProfileMenu = () => {
	const { user } = useUserContext();
	const navigate = useNavigate();
	const signoutFn = useMutation(['signout'], signout, {
		onSuccess() {
			window.open(`${STUDENT_APP_ENDPOINT}/auth/signin?redirect_url=${encodeURIComponent(window.location.href)}`, '_self');
		},
	});
	if (!user) return null;
	const initials = stringAvatar((user.name || '').trim().toUpperCase());
	return (
		<div className="[&_div[data-testid='flowbite-dropdown']]:!border-gray-200 dark:[&_div[data-testid='flowbite-dropdown']]:!border-gray-600">
			<Dropdown
				arrowIcon={false}
				inline
				dismissOnClick
				className={PROFILE_MENU_CLASS}
				label={
					<Avatar
						data-testid="drive-header-avatar"
						className="[&_img]:border"
						alt="User Profile"
						placeholderInitials={initials}
						rounded
						img={user.profile?.proctoringImageUrl || ''}
					/>
				}
			>
				<Dropdown.Header className={PROFILE_MENU_HEADER_CLASS}>
					<span className="block max-w-[260px] break-words text-[16px] font-[700] text-[#111E2C] dark:text-white">{user.name}</span>
					{user.email ? <span className="block truncate text-[13px] font-medium text-[#6B6D85]">{user.email}</span> : null}
				</Dropdown.Header>
				{user.handler ? (
					<Dropdown.Item className={PROFILE_MENU_ITEM_CLASS} onClick={() => navigate('/profile/edit')}>
						<HiOutlineUser className="h-[18px] w-[18px] text-[#8A8BA3]" />
						My Profile
					</Dropdown.Item>
				) : null}
				<Dropdown.Item className={PROFILE_MENU_DANGER_ITEM_CLASS} onClick={() => signoutFn.mutate()}>
					<HiOutlineLogout className="h-[18px] w-[18px]" />
					Logout
				</Dropdown.Item>
			</Dropdown>
		</div>
	);
};

// Desktop back link: always an icon on a soft background, never a bare arrow.
export const BackLink = ({ onClick, children, className = '' }: { onClick: () => void; children: React.ReactNode; className?: string }) => (
	<button onClick={onClick} className={`group items-center gap-2.5 text-[14px] font-[600] text-[#3A3B52] hover:text-[#0F1626] ${className || 'inline-flex'}`}>
		<span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F1F1F8] text-[#3A3B52] transition-colors group-hover:bg-[#E6E6F4]">
			<HiOutlineArrowLeft className="h-[18px] w-[18px]" />
		</span>
		{children}
	</button>
);

// Sticky top bar with the back button, mobile only.
export const MobileHeader = ({ title, onBack }: { title: string; onBack: () => void }) => (
	<div className="lg:hidden sticky top-0 z-30 -mx-4 mb-2 flex items-center gap-3 bg-[#F7F9FB] dark:bg-[#111827] px-4 py-2">
		<button
			aria-label="Back"
			onClick={onBack}
			className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F1F1F5] dark:bg-[#1F2937] text-[#111E2C] dark:text-white"
		>
			<HiOutlineArrowLeft className="h-5 w-5" />
		</button>
		<span className="flex-1 min-w-0 truncate text-[17px] font-[600] text-[#111E2C] dark:text-white">{title}</span>
		<ProfileMenu />
	</div>
);

export type PillTone = 'good' | 'wait' | 'stop' | 'brand' | 'neutral';

const TONE_CLASSES: Record<PillTone, { pill: string; dot: string }> = {
	good: { pill: 'bg-[#E6F6EC] text-[#1E7A44]', dot: 'bg-[#2E9B5B]' },
	wait: { pill: 'bg-[#FFF6D6] text-[#8A6100]', dot: 'bg-[#E0A100]' },
	stop: { pill: 'bg-[#FDECEC] text-[#B42318]', dot: 'bg-[#E5484D]' },
	brand: { pill: 'bg-[#EEEDFD] text-[#3730A3]', dot: 'bg-[#4F46E5]' },
	neutral: { pill: 'bg-[#F1F1F5] text-[#555770]', dot: 'bg-[#8A8BA3]' },
};

// Soft, tinted status pill with a leading dot, used on application cards.
export const TonePill = ({ tone, children, className = '' }: { tone: PillTone; children: React.ReactNode; className?: string }) => (
	<span className={`inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-[12px] font-[700] ${TONE_CLASSES[tone].pill} ${className}`}>
		<span className={`h-1.5 w-1.5 rounded-full ${TONE_CLASSES[tone].dot}`} />
		{children}
	</span>
);

// The app is served under /jobseeker everywhere except local dev (see App.tsx).
export const appBasePath = () => (process.env.NODE_ENV === 'development' ? '' : '/jobseeker');

// Header for logged out visitors of the drive pages: just the LevelUp logo.
export const PublicDriveHeader = () => (
	<div className="sticky top-0 z-40 flex h-[64px] items-center border-b border-[#EEEEF5] bg-white/90 px-4 backdrop-blur md:px-[45px]">
		<img src="https://masai-website-images.s3.ap-south-1.amazonaws.com/Levelup-Logo.png" width="110" height="32" alt="LevelUp" />
	</div>
);

const BACKDROP_STYLE = `
@keyframes dp-drift-a { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(40px,30px) scale(1.12); } }
@keyframes dp-drift-b { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-50px,-24px) scale(1.08); } }
@keyframes dp-twinkle { 0%,100% { opacity: .15; transform: scale(.7); } 50% { opacity: .7; transform: scale(1); } }
.dp-blob-a { animation: dp-drift-a 14s ease-in-out infinite; }
.dp-blob-b { animation: dp-drift-b 17s ease-in-out infinite; }
.dp-dot { animation: dp-twinkle 4s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .dp-blob-a, .dp-blob-b, .dp-dot { animation: none !important; } }
`;

// Soft drifting colour blobs and twinkling dots behind a page. Place inside a relative, overflow-hidden wrapper.
export const AnimatedBackdrop = () => (
	<div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
		<style dangerouslySetInnerHTML={{ __html: BACKDROP_STYLE }} />
		<div className="dp-blob-a absolute -left-24 top-10 h-[360px] w-[360px] rounded-full bg-[#DAD7FB] opacity-60 blur-[90px]" />
		<div className="dp-blob-b absolute -right-20 top-[35%] h-[320px] w-[320px] rounded-full bg-[#CFE6FB] opacity-60 blur-[90px]" />
		<div className="dp-blob-a absolute bottom-0 left-[38%] h-[260px] w-[260px] rounded-full bg-[#EAD8FA] opacity-50 blur-[90px]" />
		{[
			['12%', '22%', '0s'],
			['86%', '16%', '1.2s'],
			['78%', '70%', '2.1s'],
			['20%', '78%', '0.6s'],
			['92%', '45%', '2.9s'],
		].map(([left, top, delay]) => (
			<span key={`${left}${top}`} className="dp-dot absolute h-1.5 w-1.5 rounded-full bg-[#7B72F2]" style={{ left, top, animationDelay: delay }} />
		))}
	</div>
);
